import { useQuery } from "@tanstack/react-query";

import type { AdvisorBriefing } from "../api/advisorBriefingApi";
import { advisorBriefingApi } from "../api/advisorBriefingApi";
import { getPersona } from "@/shared/brand/persona";
import { getBrand } from "@/shared/brand/store";

/** 简报拉取超时(ms):超过即降级(骨架 → 纯前端问候),老板头号靶是「开屏白」,不许干等。 */
export const BRIEFING_TIMEOUT_MS = 3000;

/** 按本地时段拼的**纯前端**降级问候(不带读数,不冒充 ai 人话 —— factsAnchored 恒 false 的那类)。 */
export function fallbackGreeting(now: Date = new Date()): string {
  // OEM(W3 C-F):渠道配了军师开场白(brand.advisorGreeting)就用它;直营为空 → 时段通用句不变。
  const custom = getBrand().advisorGreeting?.trim();
  if (custom) return custom;
  const h = now.getHours();
  const slot = h < 5 ? "夜里好" : h < 11 ? "早" : h < 14 ? "中午好" : h < 18 ? "下午好" : "晚上好";
  return `老板,${slot}。想让我干点什么,直接说一句就行。`;
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("briefing timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

/**
 * e2e/联调 mock 旁路:URL 带 ?briefingMock=rich|cold 时返回固定夹具,不打网络。
 * 用途:①ai 端点未实做前 console 先行开发/演示;②真浏览器 e2e 两态截图(:5276 隔离栈无 ai 实做)。
 * ai 端点上线后本旁路保留当测试钩子 —— 它只由显式 URL 参数触发,正常用户路径永远走真拉取。
 */
export function briefingMockFromUrl(search: string): AdvisorBriefing | null {
  const v = new URLSearchParams(search).get("briefingMock");
  if (v === "cold") {
    return {
      kind: "briefing",
      mode: "cold_start",
      greeting: {
        text: `老板,下午好。我是${getPersona().title}${getPersona().name},店里的活一句话派给我就行。`,
        factsAnchored: false,
      },
      runningItems: [],
      chips: [
        { id: "chip-g1", text: "给我的店做一张开业海报", source: "generic" },
        { id: "chip-g2", text: "把店里的招牌产品拍成短视频", source: "generic" },
        { id: "chip-g3", text: "写一篇朋友圈种草文案", source: "generic" },
      ],
      generatedAt: "2026-08-18T02:00:00",
    };
  }
  if (v === "rich") {
    return {
      kind: "briefing",
      mode: "personalized",
      greeting: {
        text: "王老板,早。昨晚轮播 9 张出了 6,3 张卡在审核;周五活动物料还没排——先盯哪头?",
        factsAnchored: true,
      },
      runningItems: [
        {
          id: "wk-a1b2c3",
          kind: "poster",
          title: "国庆轮播 9 张",
          state: "partial",
          line: "出了 6 张,3 张卡在审核",
          route: "/works?focus=wk-a1b2c3",
        },
        {
          id: "wk-d4e5f6",
          kind: "video",
          title: "可颂新品短视频",
          state: "running",
          line: "正在出片,大约还要 3 分钟",
          route: "/works?focus=wk-d4e5f6",
        },
      ],
      chips: [
        { id: "chip-1", text: "把周五活动的物料排一下", source: "profile" },
        { id: "chip-2", text: "看看卡审核的 3 张海报怎么了", source: "recent" },
        { id: "chip-3", text: "再来一条可颂的种草文案", source: "recent" },
      ],
      generatedAt: "2026-08-18T02:00:00",
    };
  }
  return null;
}

/**
 * 开屏简报:每次挂载拉一次,不轮询(契约回执②)。失败/超时不抛给页面 ——
 * 降级语义在消费方:greeting 用 fallbackGreeting,runningItems/chips 整块隐藏。
 */
export function useAdvisorBriefing(enabled: boolean) {
  const mock = typeof window !== "undefined" ? briefingMockFromUrl(window.location.search) : null;
  return useQuery<AdvisorBriefing>({
    queryKey: ["advisor", "briefing"],
    queryFn: () =>
      mock ? Promise.resolve(mock) : withTimeout(advisorBriefingApi.get(), BRIEFING_TIMEOUT_MS),
    enabled,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });
}
